import { ConvertUtils } from "@dooboostore/core";
import { BehaviorSubject } from "@dooboostore/core";
import { Expression } from "@dooboostore/core";
import { ValidUtils } from "@dooboostore/core";
const ROUTER_NO_EVENT_MARKER = "__ROUTER_NO_EVENT__";
class Router {
  constructor(config) {
    this.leaveGuards = /* @__PURE__ */ new Set();
    this._config = config;
    let routeData;
    if (config?.firstUrl) {
      const path = config.firstUrl.split("?");
      const urlSearchParams = new URLSearchParams(path[1] ?? "");
      routeData = {
        url: config.firstUrl,
        path: path[0],
        searchParams: urlSearchParams,
        router: this,
        search: urlSearchParams.size > 0 ? `?${urlSearchParams.toString()}` : "",
        triggerPoint: "first-end"
      };
      this.behaviorSubject = new BehaviorSubject(routeData);
      this.go(config.firstUrl);
    } else {
      routeData = { ...this.getRouteData(), triggerPoint: "first-end" };
      this.behaviorSubject = new BehaviorSubject(routeData);
    }
    this.config.window.addEventListener("popstate", (event) => {
      const publish = () => this.behaviorSubject.next({ ...this.getRouteData(), triggerPoint: "end" });
      if (!this.leaveGuards.size) return publish();
      const from = this.value;
      void this.canLeave(this.getRouteData(), from).then((ok) => {
        if (ok) publish();
        else this.config.window.history?.pushState(from.data ?? null, "", from.url);
      });
    });
  }
  get value() {
    return this.behaviorSubject.getValue();
  }
  /**
   * 이동 전 가드 등록. go() 와 뒤로/앞으로 가기(popstate)에 적용된다 (pushState/replaceState 직접 호출은 제외).
   * 리턴한 함수를 부르면 해제된다.
   */
  addLeaveGuard(guard) {
    this.leaveGuards.add(guard);
    return () => {
      this.leaveGuards.delete(guard);
    };
  }
  /** 가드가 하나라도 있는지 (없으면 호출자가 동기로 진행해도 된다) */
  get hasLeaveGuards() {
    return this.leaveGuards.size > 0;
  }
  /** 등록된 가드를 순서대로 평가. 하나라도 false 면 false */
  async canLeave(to, from) {
    for (const guard of [...this.leaveGuards]) {
      if (await guard(to, from) === false) return false;
    }
    return true;
  }
  get observable() {
    return this.behaviorSubject.asObservable();
  }
  get searchParamObject() {
    return ConvertUtils.toObject(this.getSearchParams());
  }
  getSearchParamObject() {
    return this.searchParamObject;
  }
  get config() {
    return this._config;
  }
  getSearchFirstParamsObject(defaultValue) {
    const a = ConvertUtils.toObject(this.getSearchParams(), { firstValue: true });
    return Object.assign({}, defaultValue, a);
  }
  getHashSearchFirstParamsObject() {
    return ConvertUtils.toObject(this.getHashSearchParams(), { firstValue: true });
  }
  // async attach(): Promise<void> {
  //   // const proxy = getDomRenderProxy(this.config.rootObject)
  //   // if (proxy) {
  //   //   const key = `___${EventManager.ROUTER_VARNAME}`;
  //   //   await proxy.render(key);
  //   // }
  // }
  testRegexp(regexp) {
    if (regexp) {
      const b = RegExp(regexp).test(this.getPathName());
      return b;
    } else {
      return false;
    }
  }
  test(urlExpression) {
    if (urlExpression && this.getPathData(urlExpression)) {
      return true;
    } else {
      return false;
    }
  }
  getRouteData(config) {
    let path = this.getPathName();
    let url = this.getUrl();
    let searchParams = this.getSearchParams();
    let search = this.getSearch();
    if (config?.pathOrUrl) {
      const paths = config.pathOrUrl.split("?");
      search = paths[1] ? `?${paths[1]}` : "";
      searchParams = new URLSearchParams(search);
      const firstUrl = paths[0];
      if (ValidUtils.isUrl(firstUrl)) {
        path = new URL(firstUrl).pathname;
      } else {
        path = firstUrl;
      }
      url = config.pathOrUrl;
    }
    const newVar = {
      path,
      url,
      searchParams,
      // 이렇게 하면 진짜 문자열이 변형이 될수 있다 따라서 그냥 문자열 자른 원본을넣어줘야한다
      // search: searchParams.size > 0 ? `?${searchParams.toString()}` : '',
      search,
      router: this
    };
    const data = this.getData();
    if (data) {
      newVar.data = data;
    }
    if (config?.urlExpression) {
      const data2 = this.getPathData(config.urlExpression);
      if (data2) {
        newVar.pathData = data2;
      }
    }
    return Object.freeze(newVar);
  }
  pushState(data, title, path, config) {
    const stateData = config?.noEventAndPublish ? { ...data, [ROUTER_NO_EVENT_MARKER]: true } : data;
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData({ pathOrUrl: path }), triggerPoint: "start" });
    }
    this.config.window.history?.pushState(stateData, title ?? "", path);
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData({ pathOrUrl: path }), triggerPoint: "end" });
    }
    return { data: stateData };
  }
  replaceState(data, title, path, config) {
    const stateData = config?.noEventAndPublish ? { ...data, [ROUTER_NO_EVENT_MARKER]: true } : data;
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData({ pathOrUrl: path }), triggerPoint: "start" });
    }
    this.config.window.history?.replaceState(stateData, title ?? "", path);
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData({ pathOrUrl: path }), triggerPoint: "end" });
    }
    return { data: stateData };
  }
  getData() {
    return this.config.window.history?.state;
  }
  getPathData(urlExpression, currentUrl = this.getPathName()) {
    return Expression.Path.pathNameData(currentUrl, urlExpression);
  }
  back(config) {
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData(), triggerPoint: "start" });
    }
    this.config.window.history.back();
  }
  forward(config) {
    if (!config?.noEventAndPublish) {
      this.behaviorSubject.next({ ...this.getRouteData(), triggerPoint: "start" });
    }
    this.config.window.history?.forward();
  }
  go(config, changeStateConfig) {
    if (typeof config === "number") {
      if (!changeStateConfig?.noEventAndPublish) {
        this.behaviorSubject.next({ ...this.getRouteData(), triggerPoint: "start" });
      }
      this.config.window.history?.go(config);
      return;
    }
    if (typeof config === "string") {
      config = { path: config };
    }
    const target = config;
    const navigate = () => {
      if (target?.replace) {
        this.replace(target.path, target.state);
      } else {
        this.push(target.path, target.state);
      }
      if (target.scrollToTop !== false) {
        try {
          this._config.window?.scrollTo?.(0, 0);
        } catch (e) {
        }
      }
    };
    if (!this.leaveGuards.size) return navigate();
    return this.canLeave(this.getRouteData({ pathOrUrl: this.toUrl(target.path) }), this.value).then((ok) => {
      if (ok) navigate();
    });
  }
  toUrl(data) {
    let targetPath;
    if (typeof data === "string") {
      targetPath = data;
    } else {
      const tpath = data.path ?? this.getPathName();
      const s = data.searchParams ? ConvertUtils.toRawQueryString(data.searchParams, { valueEncodeURIComponent: true }) : "";
      targetPath = `${tpath}${s.length > 0 ? "?" : ""}${s}`;
    }
    return targetPath;
  }
  reload() {
    const currentUrl = this.getUrl();
    this.replace(currentUrl);
  }
}
export {
  Router
};
//# sourceMappingURL=Router.js.map
